import { faBox } from "@fortawesome/free-solid-svg-icons/faBox";
import { faFile } from "@fortawesome/free-solid-svg-icons/faFile";
import { faFlask } from "@fortawesome/free-solid-svg-icons/faFlask";
import { faImage } from "@fortawesome/free-solid-svg-icons/faImage";
import { faMicroscope } from "@fortawesome/free-solid-svg-icons/faMicroscope";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons/faPaperclip";
import { faVials } from "@fortawesome/free-solid-svg-icons/faVials";
import { FontAwesomeIcon, type FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { useTheme } from "@mui/material/styles";
import type React from "react";
import BenchIcon from "../assets/graphics/RecordTypeGraphics/Icons/Bench";
import TemplateIcon from "../assets/graphics/RecordTypeGraphics/Icons/Template";
import type { RecordIconData } from "../stores/definitions/BaseRecord";
import CustomTooltip from "./CustomTooltip";

type RecordTypeIconArgs = {
  record: RecordIconData;
  color?: string | null;
  "aria-hidden"?: boolean;
  disableTooltip?: boolean;
  style?: FontAwesomeIconProps["style"];
};

export default function RecordTypeIcon({
  record,
  color, // Empty string signifies default styling
  "aria-hidden": ariaHidden,
  disableTooltip = false,
  style,
}: RecordTypeIconArgs): React.ReactNode {
  const theme = useTheme();

  // biome-ignore lint/suspicious/noImplicitAnyLet: initial biome migration
  let icon;
  switch (record.iconName) {
    case "bench":
      icon = <BenchIcon color={color ?? theme.palette.record.container.fg} />;
      break;
    case "container":
      icon = (
        <FontAwesomeIcon size="1x" icon={faBox} color={color ?? theme.palette.record.container.fg} style={style} />
      );
      break;
    case "sample":
      icon = <FontAwesomeIcon size="1x" icon={faFlask} color={color ?? theme.palette.record.sample.fg} style={style} />;
      break;
    case "subsample":
      icon = (
        <FontAwesomeIcon size="1x" icon={faVials} color={color ?? theme.palette.record.subSample.fg} style={style} />
      );
      break;
    case "instrument":
      icon = (
        <FontAwesomeIcon
          size="1x"
          icon={faMicroscope}
          color={color ?? theme.palette.record.instrument.fg}
          style={style}
        />
      );
      break;
    case "template":
      icon = (
        <TemplateIcon
          sx={{
            color: color ?? theme.palette.record.sampleTemplate.fg,
            ...style,
          }}
        />
      );
      break;
    case "instrumentTemplate":
      icon = (
        <TemplateIcon
          sx={{
            color: color ?? theme.palette.record.instrumentTemplate.fg,
            ...style,
          }}
        />
      );
      break;
    case "attachment":
      icon = (
        <FontAwesomeIcon
          size="1x"
          icon={faPaperclip}
          color={color ?? theme.palette.record.attachment.fg}
          style={style}
        />
      );
      break;
    case "document":
      icon = <FontAwesomeIcon size="1x" icon={faFile} color={color ?? theme.palette.record.document.fg} />;
      break;
    case "gallery":
      icon = <FontAwesomeIcon size="1x" icon={faImage} color={color ?? theme.palette.record.gallery.fg} />;
      break;
    default:
      throw new Error(`Unknown icon ${record.iconName}`);
  }

  if (disableTooltip || record.recordTypeLabel === "") return icon;
  return (
    <CustomTooltip title={record.recordTypeLabel} aria-hidden={ariaHidden}>
      {icon}
    </CustomTooltip>
  );
}
